import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import { FiCalendar, FiSearch } from 'react-icons/fi';
import '../../styles/AssignReportModal.css';

const AssignReportModal = ({ isOpen, onClose, dogId, templates, onAssigned, dogs = [] }) => {
    const [formData, setFormData] = useState({
        reportTemplateId: '',
        dogId: dogId || '',
        startDate: '',
        endDate: '',
        recurrence: 'ONCE'
    });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredDogs, setFilteredDogs] = useState(dogs);

    // Filter dogs when search term changes
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredDogs(dogs);
            return;
        }

        const searchTermLower = searchTerm.toLowerCase();
        const filtered = dogs.filter(dog =>
            dog.name.toLowerCase().includes(searchTermLower) ||
            (dog.breed && dog.breed.toLowerCase().includes(searchTermLower)) ||
            (dog.ownersName && dog.ownersName.toLowerCase().includes(searchTermLower))
        );

        setFilteredDogs(filtered);
    }, [searchTerm, dogs]);

    // Recurrence options in Polish
    const recurrenceOptions = [
        { value: 'ONCE', label: 'Jednorazowo' },
        { value: 'DAILY', label: 'Codziennie' },
        { value: 'EVERY_2_DAYS', label: 'Co 2 dni' },
        { value: 'EVERY_3_DAYS', label: 'Co 3 dni' },
        { value: 'EVERY_4_DAYS', label: 'Co 4 dni' },
        { value: 'EVERY_5_DAYS', label: 'Co 5 dni' },
        { value: 'EVERY_6_DAYS', label: 'Co 6 dni' },
        { value: 'WEEKLY', label: 'Tygodniowo' }
    ];

    // Reset form
    const resetForm = () => {
        setFormData({
            reportTemplateId: '',
            dogId: dogId || '',
            startDate: '',
            endDate: '',
            recurrence: 'ONCE'
        });
        setSearchTerm('');
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!formData.reportTemplateId || !formData.startDate || !formData.dogId) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            const response = await axios.post('/api/v1/reports/assignments', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            onAssigned(response.data);
            onClose();
            resetForm();


        } catch (err) {
            console.error('Error assigning report:', err);
            const errorMessage = err.response?.data?.message || 'Nie udało się przypisać raportu';
        } finally {
            setLoading(false);
        }
    };

    // Get current date in YYYY-MM-DD format for date input
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    if (!isOpen) return null;

    return (
        <div className="assign-modal-overlay" onClick={onClose}>
            <div className="assign-modal-content" onClick={e => e.stopPropagation()}>
                <div className="assign-modal-header">
                    <h2>Przypisz raport</h2>
                    <button
                        className="assign-modal-close"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="assign-modal-body">
                    <div className="assign-form-group">
                        <label htmlFor="reportTemplate">Wybierz szablon raportu</label>
                        <select
                            id="reportTemplate"
                            className="assign-form-select"
                            value={formData.reportTemplateId}
                            onChange={(e) => setFormData({ ...formData, reportTemplateId: e.target.value })}
                        >
                            <option value="">Wybierz szablon</option>
                            {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Show dog selection dropdown only if no dogId was provided */}
                    {!dogId && (
                        <div className="assign-form-group">
                            <label htmlFor="dogSelect">Pupil</label>

                            {/* Search bar for filtering dogs */}
                            <div className="assign-search-container">
                                <div className="assign-search-wrapper">
                                    <FiSearch className="assign-search-icon" />
                                    <input
                                        type="text"
                                        className="assign-search-input"
                                        placeholder="Szukaj pupila..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <select
                                id="dogSelect"
                                className="assign-form-select"
                                value={formData.dogId}
                                onChange={(e) => setFormData({ ...formData, dogId: e.target.value })}
                                size={filteredDogs.length > 5 ? 5 : Math.max(2, filteredDogs.length)}
                            >
                                {filteredDogs.map((dog) => (
                                    <option key={dog.id} value={dog.id}>
                                        {dog.name} ({dog.breed}) - {dog.ownersName}
                                    </option>
                                ))}
                            </select>

                            {filteredDogs.length === 0 && searchTerm && (
                                <div className="assign-no-results">
                                    Nie znaleziono pupilów dla "{searchTerm}"
                                </div>
                            )}
                        </div>
                    )}

                    <div className="assign-form-group">
                        <label htmlFor="startDate">Data rozpoczęcia</label>
                        <input
                            id="startDate"
                            type="date"
                            className="assign-form-input"
                            min={getTodayDate()}
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="endDate">Data zakończenia (Opcjonalnie)</label>
                        <input
                            id="endDate"
                            type="date"
                            className="assign-form-input"
                            min={getTodayDate()}
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="recurrence">Powtarzalność</label>
                        <select
                            id="recurrence"
                            className="assign-form-select"
                            value={formData.recurrence}
                            onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                        >
                            {recurrenceOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="assign-modal-footer">
                    <button
                        className="assign-cancel-btn"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Anuluj
                    </button>
                    <button
                        className="assign-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading || !formData.reportTemplateId || !formData.startDate || !formData.dogId}
                    >
                        {loading ? 'Przypisywanie...' : 'Przypisz'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignReportModal;